import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { ProfileProvider } from '../hooks/useProfile';
import { ProfileSection } from '../components/MyPage/ProfileSection';
import { Loading } from '../components/UI/Loading';
import { useAuth } from '../hooks/useAuth';
import { MainLayout } from '../components/Layout/MainLayout';

export const MyPage: React.FC = () => {
  const { user, isLoading, error } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    // Set page title
    document.title = 'ガクフドキャリア';
  }, []);

  if (isLoading) {
    return (
      <MainLayout showHeader={isMobile} title="ガクフドキャリア">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Loading variant="circular" size="large" />
        </Container>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout showHeader={isMobile} title="ガクフドキャリア">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout showHeader={isMobile} title="ガクフドキャリア">
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            ユーザー情報が見つかりません。再度ログインしてください。
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout showHeader={isMobile} title="ガクフドキャリア">
      <ProfileProvider>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            aria-label="breadcrumb" 
            sx={{ 
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}
          >
            <Link
              underline="hover"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              }}
              color="inherit"
              href="/"
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: { xs: 'small', sm: 'inherit' } }} />
              ホーム
            </Link>
            <Typography
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              }}
              color="text.primary"
            >
              <PersonIcon sx={{ mr: 0.5, fontSize: { xs: 'small', sm: 'inherit' } }} />
              マイページ
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                fontWeight: 'bold',
              }}
            >
              マイページ
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              プロフィール情報の管理とアカウント設定を行えます。
            </Typography>
          </Box>

          {/* Profile Section */}
          <ProfileSection />
        </Container>
      </ProfileProvider>
    </MainLayout>
  );
};

export default MyPage;